import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Select, Button } from "antd";
import { Icon } from "@iconify/react";
import { Button as AppButton } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
// A plain "YYYY-MM-DD" is a calendar day, so it's built in local time. `new Date("YYYY-MM-DD")`
// reads it as UTC midnight, which west of UTC (e.g. the US) is the previous evening: picking
// Saturday the 10th then showed and highlighted Friday the 9th.
export function parseDateValue(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
}
type DateRule = "past-and-today" | "before-today" | "today-onward";

/**
 * The picked date carried to another month/year (Month or Year dropdown):
 * same day number, clamped to that month's length (31 Jan → 28/29 Feb), and
 * if that day isn't allowed, the nearest allowed day in that same month.
 * null when the month has no allowed day at all (it just browses then).
 */
export function carryDate(selected: Date, year: number, month: number, rule: DateRule, today: Date): Date | null {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const candidate = new Date(year, month, Math.min(selected.getDate(), daysInMonth));
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const allowed = (d: Date) =>
    rule === "today-onward" ? d >= today : rule === "before-today" ? d < today : d <= today;
  if (allowed(candidate)) return candidate;
  const boundary = rule === "before-today" ? yesterday : today;
  return boundary.getFullYear() === year && boundary.getMonth() === month ? boundary : null;
}

function formatDate(date: string) {
  const parsed = parseDateValue(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(parsed);
}

interface CalendarDateFieldProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disablePast?: boolean;
  disableToday?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** No trigger of its own: the calendar opens in place, centred on the
   *  spot it's rendered in (the parent decides when via `open`). */
  autoExpand?: boolean;
  /** Removes the trigger's own surface when it is rendered inside an existing field shell. */
  bare?: boolean;
}

export function CalendarDateField({
  value,
  placeholder = "Select date",
  onChange,
  disablePast = false,
  disableToday = false,
  open: openProp,
  onOpenChange,
  autoExpand = false,
  bare = false,
}: CalendarDateFieldProps) {
  const today = startOfDay(new Date());
  // Current month, not next — disablePast means "today onward" is selectable, so today's
  // own month must stay browsable (and visible by default; see `baseDate` below).
  const earliestBrowsable = disablePast ? new Date(today.getFullYear(), today.getMonth(), 1) : null;
  const baseDate = value ? parseDateValue(value) : earliestBrowsable ?? today;
  const initialDate = Number.isNaN(baseDate.getTime()) ? today : baseDate;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (!isControlled) setInternalOpen(next);
  };

  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const selectedDate = value ? startOfDay(parseDateValue(value)) : null;

  // The calendar opens centred on the spot that was clicked (the field's own
  // centre when opened from the keyboard or by the parent), floating over the
  // page with no backdrop. Near a screen edge it shifts just enough to stay
  // fully on screen; on a screen shorter than the calendar it caps its height
  // and scrolls inside instead of running off the top or bottom.
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const clickPoint = useRef<{ x: number; y: number } | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; maxHeight?: number } | null>(null);
  const EDGE_MARGIN = 16;
  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    const place = () => {
      const panel = panelRef.current;
      const anchor = triggerRef.current;
      if (!panel || !anchor) return;
      const rect = anchor.getBoundingClientRect();
      const point = clickPoint.current ?? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      const width = panel.offsetWidth;
      const fullHeight = panel.scrollHeight;
      const roomY = window.innerHeight - EDGE_MARGIN * 2;
      const height = Math.min(fullHeight, roomY);
      const clamp = (value: number, size: number, viewport: number) =>
        Math.min(Math.max(value, EDGE_MARGIN), Math.max(viewport - size - EDGE_MARGIN, EDGE_MARGIN));
      setPanelPos({
        left: clamp(point.x - width / 2, width, window.innerWidth),
        top: clamp(point.y - height / 2, height, window.innerHeight),
        maxHeight: fullHeight > roomY ? roomY : undefined,
      });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  // Closes on a click anywhere outside the calendar (the field's own click
  // toggles it) and on Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // This component stays mounted across close/reopen, so viewMonth/viewYear would otherwise stay
  // wherever the user last left them — reopening a field that already has a
  // value could show a different month than the selected date. Jump back to
  // the selected date's (or today's) month every time the calendar opens.
  useEffect(() => {
    if (!open) return;
    const jumpTo = value ? startOfDay(parseDateValue(value)) : (earliestBrowsable ?? today);
    if (Number.isNaN(jumpTo.getTime())) return;
    setViewMonth(jumpTo.getMonth());
    setViewYear(jumpTo.getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const years = earliestBrowsable
    ? Array.from({ length: 11 }, (_, i) => earliestBrowsable.getFullYear() + i)
    : Array.from({ length: 120 }, (_, i) => today.getFullYear() - i);
  const monthOptions = earliestBrowsable
    ? MONTH_LABELS.map((label, index) => ({ label, index })).filter(
        ({ index }) => viewYear > earliestBrowsable.getFullYear() || index >= earliestBrowsable.getMonth(),
      )
    : MONTH_LABELS.map((label, index) => ({ label, index }));
  const isPrevMonthDisabled = Boolean(
    earliestBrowsable && viewYear === earliestBrowsable.getFullYear() && viewMonth <= earliestBrowsable.getMonth(),
  );

  const firstDay = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayOffset = index - firstDay.getDay() + 1;
    const date = new Date(viewYear, viewMonth, dayOffset);
    const inCurrentMonth = dayOffset >= 1 && dayOffset <= daysInMonth;
    const label = inCurrentMonth ? dayOffset : dayOffset < 1 ? prevMonthDays + dayOffset : dayOffset - daysInMonth;
    return { date, label, inCurrentMonth };
  });

  const moveMonth = (direction: -1 | 1) => {
    if (direction === -1 && isPrevMonthDisabled) return;
    const next = new Date(viewYear, viewMonth + direction, 1);
    setViewMonth(next.getMonth());
    setViewYear(next.getFullYear());
  };
  // Changing the Month or Year dropdown moves an already-picked date along
  // (same day, new month/year) instead of only browsing, so it isn't "lost".
  // The ‹ › arrows still only browse.
  const rule: DateRule = disablePast ? "today-onward" : disableToday ? "before-today" : "past-and-today";
  const carrySelection = (year: number, month: number) => {
    if (!selectedDate || Number.isNaN(selectedDate.getTime())) return;
    const moved = carryDate(selectedDate, year, month, rule, today);
    if (moved && moved.getTime() !== selectedDate.getTime()) onChange(toDateInputValue(moved));
  };
  const changeMonth = (nextMonth: number) => {
    setViewMonth(nextMonth);
    carrySelection(viewYear, nextMonth);
  };
  const changeYear = (nextYear: number) => {
    let nextMonth = viewMonth;
    if (earliestBrowsable && nextYear === earliestBrowsable.getFullYear() && viewMonth < earliestBrowsable.getMonth()) {
      nextMonth = earliestBrowsable.getMonth();
    }
    setViewYear(nextYear);
    setViewMonth(nextMonth);
    carrySelection(nextYear, nextMonth);
  };

  // No overflow of its own: the floating panel caps + scrolls its OUTER box,
  // and a second scroller here would show two scrollbars.
  const calendarBody = (
    <div className="w-[312px] p-2">
      {/* Title + close: the calendar can always be dismissed without picking a date. */}
      <div className="mb-3 flex items-center justify-between">
        <p className="!mb-0 text-sm font-semibold text-[#0A2947]">Select a date</p>
        <Button
          shape="circle"
          type="text"
          aria-label="Close calendar"
          onClick={() => setOpen(false)}
          icon={<Icon icon="lucide:x" className="h-4 w-4 text-[#7A7F8D]" />}
        />
      </div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <Button
          shape="circle"
          type="text"
          disabled={isPrevMonthDisabled}
          onClick={() => moveMonth(-1)}
          icon={<Icon icon="lucide:chevron-left" className="h-5 w-5 text-[#7A7F8D]" />}
        />
        <Select
          value={viewMonth}
          onChange={changeMonth}
          // Its list stays inside the calendar, so picking from it isn't an "outside" click.
          getPopupContainer={(node) => node.parentElement ?? document.body}
          className="!h-10 !w-[110px]"
          options={monthOptions.map(({ label, index }) => ({ label, value: index }))}
        />
        <Select
          value={viewYear}
          onChange={changeYear}
          getPopupContainer={(node) => node.parentElement ?? document.body}
          className="!h-10 !w-[90px]"
          options={years.map((y) => ({ label: y, value: y }))}
        />
        <Button
          shape="circle"
          type="text"
          onClick={() => moveMonth(1)}
          icon={<Icon icon="lucide:chevron-right" className="h-5 w-5 text-[#7A7F8D]" />}
        />
      </div>

      <div className="grid grid-cols-7 text-center text-sm text-[#7A7F86]">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xl">
        {cells.map(({ date, label, inCurrentMonth }) => {
          const normalized = startOfDay(date);
          const isDisabledDate = disablePast
            ? normalized < today
            : disableToday
              ? normalized >= today
              : normalized > today;
          const isSelected = selectedDate?.getTime() === normalized.getTime();
          return (
            <AppButton
              variant="ghost"
              key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
              disabled={!inCurrentMonth || isDisabledDate}
              onClick={() => { onChange(toDateInputValue(date)); setOpen(false); }}
              className={cn(
                "!h-11 !w-full !rounded-xl",
                !inCurrentMonth ? "!text-[#B8B8B8]" : "!text-[#222222]",
                // Hover: platform red at 50% (same as every brand button).
                inCurrentMonth && !isDisabledDate && "hover:!bg-[#E4002B]/50 hover:!text-white",
                // Selected day: platform red at 50%, white number.
                isSelected ? "!bg-[#E4002B]/50 !text-white" : "",
                // Sundays: #E4002B at 10%, black number like every other day.
                // A selected Sunday takes the selected look above instead.
                inCurrentMonth && !isSelected && date.getDay() === 0 ? "!bg-[#E4002B]/10 !text-[#222222]" : "",
                isDisabledDate ? "!cursor-not-allowed !opacity-35" : "",
              )}
            >
              {label}
            </AppButton>
          );
        })}
      </div>
    </div>
  );

  const trigger = (
    <AppButton
      variant="ghost"
      onClick={(event: MouseEvent<HTMLElement>) => {
        // A mouse/touch click centres the calendar on that spot; Enter/Space
        // (detail 0) centres it on the field.
        clickPoint.current = event.detail > 0 ? { x: event.clientX, y: event.clientY } : null;
        setOpen(!open);
      }}
      className={cn(
        "!flex !h-auto !w-full !items-center !justify-between !gap-2 !text-left !text-sm",
        bare
          ? "!h-full !min-w-0 !flex-1 !bg-transparent !p-0"
          : "!h-[42px] !rounded-xl !border !border-[#E5E7EB] !bg-[#F9FAFB] !px-3 focus:!border-[#E4002B]/50 focus:!shadow-none",
      )}
    >
      <span className={value ? "text-[#0A2947]" : "text-[#7A7F8D]"}>
        {value ? formatDate(value) : placeholder}
      </span>
      <Icon icon="lucide:calendar-days" className="h-4 w-4 shrink-0 text-[#7A7F8D]" />
    </AppButton>
  );

  return (
    <>
      {/* autoExpand with nothing picked has no trigger: an empty anchor where
          it's rendered, so the calendar still opens centred right there. */}
      <span ref={triggerRef} className="block w-full">
        {autoExpand && !value ? null : trigger}
      </span>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Select a date"
            // Above antd modals/drawers (z 1000) so it works inside them too.
            className="fixed z-[1100] overflow-y-auto rounded-[22px] bg-white p-6 shadow-[0_18px_40px_rgba(17,24,39,0.18)] transition-opacity duration-150"
            style={{
              top: panelPos?.top ?? 0,
              left: panelPos?.left ?? 0,
              maxHeight: panelPos?.maxHeight,
              // Measured on its first frame, then shown in place.
              opacity: panelPos ? 1 : 0,
            }}
          >
            {calendarBody}
          </div>,
          document.body,
        )}
    </>
  );
}
